/**
 * @file "Polymorphic" DocumentsContext providers for the Document components.
 *
 * Define different DocumentContext providers in order to make the data source
 * transparent for the Documents components that display them,
 * allowing them to be reused.
 *
 * The `useQuery` hook should be a `Suspense` query, so the data is not
 * `undefined`.
 *
 * To support deletion, a `DeleteComponent` needs to be provided, preferably a
 * dialog.
 */

import React from "react"

import { useCustomerRepositoryListSuspense } from "@boundless/bl-api-client/customer/services"
import { useLenderApplicationsRepositoryListSuspense } from "@boundless/bl-api-client/lender/services"

import {
  DeleteDocumentDialog,
  type DeleteDialogProps,
} from "@/components/documents/DeleteDocumentDialog"
import {
  UncategorizeDocumentComponent,
  type UncategorizeDocumentComponentProps,
} from "@/components/documents/UncategorizeDocumentComponent"
import { compareStatements } from "@/lib/utils"

import type { RepositoryDocument as CustomerDocument } from "@boundless/bl-api-client/customer/models"
import type { RepositoryDocument as LenderDocument } from "@boundless/bl-api-client/lender/models"

// Common representation between both Customer and Lender facing document
// instances.
export type CommonDocument = CustomerDocument | LenderDocument

interface DocumentsContextType {
  allDocuments: CommonDocument[]
  categorized: CommonDocument[]
  uncategorized: CommonDocument[]
  savedTypeIds: number[]
  DeleteComponent?: React.ComponentType<DeleteDialogProps>
  UncategorizeDocumentComponent?: React.ComponentType<UncategorizeDocumentComponentProps>
}

const DocumentsContext = React.createContext<DocumentsContextType | undefined>(
  undefined,
)

function getCommonDocumentContext(
  documents: CommonDocument[],
): Omit<DocumentsContextType, "DeleteComponent"> {
  const categorized = documents
    .filter((d) => d.isConfirmed)
    .sort((a, b) => {
      const aIsBanking = a.category === "banking"
      const bIsBanking = b.category === "banking"

      if (aIsBanking !== bIsBanking) {
        return aIsBanking ? -1 : 1
      }

      if (aIsBanking) {
        return compareStatements(a, b)
      }

      return (a.typeName ?? "").localeCompare(b.typeName ?? "")
    })
  const uncategorized = documents
    .filter((d) => !d.isConfirmed)
    .sort((a, b) => a.filename.localeCompare(b.filename))

  const savedTypeIds = categorized
    .map((d) => d.type)
    .filter((t): t is number => t !== undefined)

  return {
    allDocuments: documents,
    categorized,
    uncategorized,
    savedTypeIds,
  }
}

export const CustomerDocumentProvider = ({
  children,
  companyUuid,
}: {
  children: React.ReactNode
  companyUuid: string
}) => {
  const { data: documents } = useCustomerRepositoryListSuspense(companyUuid)
  const base = getCommonDocumentContext(documents)

  return (
    <DocumentsContext.Provider
      value={{
        ...base,
        DeleteComponent: DeleteDocumentDialog,
        UncategorizeDocumentComponent: UncategorizeDocumentComponent,
      }}
    >
      {children}
    </DocumentsContext.Provider>
  )
}

export const LenderDocumentProvider = ({
  children,
  applicationUuid,
}: {
  children: React.ReactNode
  applicationUuid: string
}) => {
  const { data: documents } =
    useLenderApplicationsRepositoryListSuspense(applicationUuid)
  const base = getCommonDocumentContext(documents)

  return (
    <DocumentsContext.Provider value={{ ...base }}>
      {children}
    </DocumentsContext.Provider>
  )
}

export const useDocuments = () => {
  const context = React.useContext(DocumentsContext)
  if (context === undefined) {
    throw new Error("useDocuments must be used within a DocumentsProvider")
  }
  return context
}
